import { useAuth } from "@/components/auth/AuthProvider";
import useReferralCode from "@/hooks/useReferralCode";
import { supabase } from "@/integrations/supabase/client";
import { BookEventInput, Event } from "@/types/event";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import EventCreditInfo from "./EventCreditInfo";
import BookedEventActions from "./booking/BookedEventActions";
import BookingControls from "./booking/BookingControls";
import ReferralInfo from "./booking/ReferralInfo";
import useIsValidReferralCode from "@/hooks/useIsValidReferralCode";
import useReferrerIdFromCode from "@/hooks/useReferrerIdFromCode";
import { useProfile } from "@/hooks/useProfile";
import { findCampaignForEvent } from "@/lib/campaigns";
import WaitlistMessage from "./WaitlistMessage";
import { CREDIT_VALUE_IN_POUNDS } from "@/configs";
import { formatPrice, isGbp } from "@/lib/currency";
import { useMembershipDiscountStatus, getBestMembershipDiscount } from "@/hooks/useMembershipDiscountStatus";
import { Button } from "@/components/ui/button";
import WaitlistRequestDialog from "./WaitlistRequestDialog";
import WaitlistEventActions from "./WaitlistEventActions";
import { toast } from "@/hooks/use-toast";
import { useUnlimitedStatus } from "@/hooks/useUnlimitedStatus";
import UnlimitedBookingBanner from "./booking/UnlimitedBookingBanner";
import { useTracking } from "@/hooks/useInteractionTracking";
import { Crown, ShieldAlert, Tag, Gift } from "lucide-react";
import { useEventInterest } from "@/hooks/useEventInterest";
import { capture } from "@/services/posthog";
import { trackMetaEvent, metaEventParams } from "@/services/analytics";
import { useBringFriendEligibility } from "@/hooks/useBringFriend";
import { useProspectiveMemberPrice } from "@/hooks/useFriendsUpsell";
import { getSeatCap } from "@/components/event/booking/BookingControls";
import FriendsUpsellToggle from "./booking/FriendsUpsellToggle";
import { useEventEarlyBird } from "@/hooks/useEventEarlyBird";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import DepartureSchedule from "./DepartureSchedule";
import { earliestOpenGroup, formatGroupTime } from "@/lib/eventGroups";
import { userTicketQueryKey } from "@/lib/ticketQueryKeys";


interface UrlPartnerCodeInfo {
  code: string;
  partner_name: string;
  discount_type: string;
  discount_percentage: number | null;
  credits_value: number;
  applies_to_tags: string[] | null;
  is_active: boolean;
  valid_from: string | null;
  valid_until: string | null;
  checkout_preview_text: string | null;
}

interface EventBookingProps {
  price: number;
  creditsCost?: number;
  onBook: (input: BookEventInput) => Promise<unknown>;
  isProcessing: boolean;
  event: Event;
  isSecretSalon?: boolean;
  hasInvite?: boolean;
  userHasRequiredTag?: boolean;
  matchedTagName?: string;
  urlPartnerCode?: string | null;
  urlPartnerCodeInfo?: UrlPartnerCodeInfo | null;
  /** Raw campaign code from the URL (?special_offer=), applied or not. */
  specialOfferCode?: string | null;
  selectedGroupId?: string | null;
  onSelectedGroupChange?: (groupId: string) => void;
}

const EventBooking = ({ 
  price, 
  creditsCost, 
  onBook, 
  isProcessing,
  event,
  isSecretSalon = false,
  hasInvite = false,
  userHasRequiredTag = true,
  matchedTagName,
  urlPartnerCode,
  urlPartnerCodeInfo,
  specialOfferCode,
  selectedGroupId,
  onSelectedGroupChange,
}: EventBookingProps) => {
  const selectedGroup = event.groups?.find((group) => group.group_id === selectedGroupId) ?? null;
  const remainingSeats = selectedGroup
    ? selectedGroup.spots_remaining
    : event.capacity == null ? null : Math.max(0, event.capacity - (event.booked || 0));
  const seatCap = getSeatCap(remainingSeats);
  const [seats, setSeats] = useState(() => {
    const n = Number(new URLSearchParams(window.location.search).get("seats"));
    return Number.isInteger(n) && n >= 1 ? Math.min(n, seatCap) : 1;
  });
  // If seats sell out underneath the visitor, pull their selection down to what remains.
  useEffect(() => {
    if (seats > seatCap) setSeats(seatCap);
  }, [seats, seatCap]);
  const [applyCredits, setApplyCredits] = useState(true);
  const [bookingForFriend, setBookingForFriend] = useState(false);
  const [showWaitlistDialog, setShowWaitlistDialog] = useState(false);
  const [showBookingSheet, setShowBookingSheet] = useState(false);

  useEffect(() => {
    if (event.groups?.length && !selectedGroupId) return;
    if (selectedGroupId && !event.groups?.some((group) => group.group_id === selectedGroupId && group.spots_remaining > 0)) {
      onSelectedGroupChange?.(earliestOpenGroup(event.groups ?? [])?.group_id ?? "");
    }
  }, [event.groups, onSelectedGroupChange, selectedGroupId]);

  const queryClient = useQueryClient();
  const { isOwnReferralCode, isReady, referralCode } = useReferralCode()
  const { data: isValidReferralCode, isLoading: isCheckingReferralCode } = useIsValidReferralCode(referralCode);
  const { data: referrerId } = useReferrerIdFromCode(referralCode);
  const { data: userProfile } = useProfile();
  const { isAuthenticated, loading: isLoadingAuthInfo } = useAuth();
  const navigate = useNavigate();
  const { trackClick, trackFeatureUsage } = useTracking();
  const { data: unlimitedStatus } = useUnlimitedStatus();
  const { data: memberDiscounts } = useMembershipDiscountStatus();
  const { hasInterest, registerInterest, cancelInterest, isLoading: isLoadingInterest } = useEventInterest(event.id);
  
  // Check if user was already referred by this person at signup (prevents double discount display)
  const wasReferredBySameUser = userProfile?.referred_by && referrerId && userProfile.referred_by === referrerId;

  const { data: userMembership, isFetched: membershipFetched } = useQuery({
    queryKey: ['user-membership'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('user_memberships')
        .select(`
          credits_balance,
          memberships (
            name,
            credits_per_month
          )
        `)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: isAuthenticated,
  });

  const { data: ticketInfo } = useQuery({
    queryKey: userTicketQueryKey(event.id),
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('tickets')
        .select('id')
        .eq('event_id', event.id)
        .eq('user_id', user.id)
        .eq('status', 'confirmed')
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: isAuthenticated,
  });

  const { data: bringFriendEligibility } = useBringFriendEligibility(event.id);

  const { data: waitlistInfo } = useQuery({
    queryKey: ['salon-waitlist', event.id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('salon_waitlist')
        .select('id, status')
        .eq('event_id', event.id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: isAuthenticated && isSecretSalon,
  });

  const requestWaitlistMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("salon_waitlist")
        .insert({
          user_id: user.id,
          event_id: event.id,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      trackFeatureUsage("booking", "waitlist_request_success", { eventId: event.id });
      capture("salon_waitlist_joined", { interest_type: "secret_salon" });
      queryClient.invalidateQueries({ queryKey: ["salon-waitlist", event.id] });
      setShowWaitlistDialog(true);
    },
    onError: (error) => {
      console.error("Error requesting waitlist:", error);
      trackFeatureUsage("booking", "waitlist_request_failed", { eventId: event.id });
      toast({
        title: "Error",
        description: "Failed to submit request. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleRequestWaitlist = () => {
    trackClick("request_waitlist", { eventId: event.id });
    if (!isAuthenticated) {
      const redirectTo = `/events/${event.id}`;
      const signUpPath = `/auth?action=sign-up&redirectTo=${encodeURIComponent(redirectTo)}`;
      navigate(signUpPath);
      return;
    }
    requestWaitlistMutation.mutate();
  };

  // Check allowed booking methods
  const allowedMethods = event.allowed_booking_methods || ['stripe', 'credits', 'unlimited'];
  const stripeAllowed = allowedMethods.includes('stripe');
  // Non-GBP events skip credits & unlimited (MVP: those flows are GBP-only)
  const eventIsGbp = isGbp(event.currency);
  const creditsAllowed = allowedMethods.includes('credits') && eventIsGbp;
  const unlimitedAllowed = allowedMethods.includes('unlimited') && eventIsGbp;

  // Calculate credit-related values
  const creditsBalance = userMembership?.credits_balance || 0;
  // Early bird (server-authoritative): gate on `active` — the RPC returns
  // discounted_price even when the window has passed; never coalesce it.
  // This is the single base price for the Book button, the decline line,
  // credit coverage and what Stripe charges.
  const { data: eb } = useEventEarlyBird(event.id);
  const ebActive = !!eb?.active;
  const unitPrice = ebActive && eb?.discounted_price != null ? Number(eb.discounted_price) : price;
  const unitCreditCost = ebActive && eb?.discounted_credit_cost != null
    ? Number(eb.discounted_credit_cost)
    : (creditsCost || 0);
  const totalPrice = unitPrice * seats;
  const totalCreditCost = unitCreditCost * seats;

  // Check if URL partner code applies to this event
  const eventTags = event.tags || [];
  const urlPartnerApplies = urlPartnerCodeInfo && urlPartnerCodeInfo.is_active && (
    !urlPartnerCodeInfo.applies_to_tags ||
    urlPartnerCodeInfo.applies_to_tags.includes('all') ||
    urlPartnerCodeInfo.applies_to_tags.some((tag: string) =>
      eventTags.map(t => t.toLowerCase()).includes(tag.toLowerCase())
    )
  );

  // Check if any membership discount applies to this event
  const memberDiscountMatch = getBestMembershipDiscount(memberDiscounts || [], eventTags);
  const memberDiscountApplies = !!memberDiscountMatch;

  // Calculate effective price after URL partner discount (used for credit coverage check)
  let effectivePrice = totalPrice;
  let effectiveCreditCost = totalCreditCost;
  if (urlPartnerApplies && urlPartnerCodeInfo) {
    if (urlPartnerCodeInfo.discount_type === 'percentage' && urlPartnerCodeInfo.discount_percentage) {
      const factor = 1 - urlPartnerCodeInfo.discount_percentage / 100;
      effectivePrice = effectivePrice * factor;
      effectiveCreditCost = Math.ceil(effectiveCreditCost * factor);
    } else if (urlPartnerCodeInfo.discount_type === 'credits') {
      effectivePrice = Math.max(0, effectivePrice - urlPartnerCodeInfo.credits_value * CREDIT_VALUE_IN_POUNDS);
    }
  }
  
  // Apply membership discount to credit cost and price (when no URL partner code)
  if (!urlPartnerApplies && memberDiscountApplies && memberDiscountMatch) {
    const factor = 1 - memberDiscountMatch.discount.discount_percentage / 100;
    effectiveCreditCost = Math.ceil(effectiveCreditCost * factor);
    effectivePrice = effectivePrice * factor;
  }
  
  // Calculate credits to use against the discounted price
  // Hard-gate on creditsAllowed: non-GBP events must never consume credits.
  const creditsToUse = creditsAllowed && applyCredits ? Math.min(creditsBalance, effectiveCreditCost) : 0;
  const creditValueUsed = creditsToUse * CREDIT_VALUE_IN_POUNDS;
  const remainingAfterCredits = Math.max(0, effectivePrice - creditValueUsed);
  const isFullyCoveredByCredits = creditsAllowed && applyCredits && creditsToUse > 0 && remainingAfterCredits <= 0;

  // For credit-only events, force credit usage
  const isCreditOnlyEvent = creditsAllowed && !stripeAllowed;
  const effectiveApplyCredits = creditsAllowed && (isCreditOnlyEvent || applyCredits);
  const effectiveCreditsToUse = !creditsAllowed
    ? 0
    : isCreditOnlyEvent
      ? Math.min(creditsBalance, effectiveCreditCost)
      : creditsToUse;
  const effectiveIsFullyCovered = !creditsAllowed
    ? false
    : isCreditOnlyEvent
      ? effectiveCreditsToUse > 0 && effectivePrice <= (effectiveCreditsToUse * CREDIT_VALUE_IN_POUNDS)
      : isFullyCoveredByCredits;

  const handleUserBooking = () => {
    // Only use unlimited booking if event is within the unlimited period, unlimited is allowed, and NOT booking for friend
    const shouldUseUnlimited = isUnlimited && isEventWithinUnlimitedPeriod && unlimitedAllowed && !bookingForFriend;
    const bookingSeats = shouldUseUnlimited ? 1 : seats;
    
    // Track booking attempt
    trackFeatureUsage("booking", "book_attempt", { 
      eventId: event.id, 
      eventTitle: event.title,
      seats: bookingSeats,
      useCredits: effectiveIsFullyCovered,
      useUnlimited: shouldUseUnlimited,
      forFriend: bookingForFriend,
    });

    capture("booking_started", {
      event_id: event.id,
      method: shouldUseUnlimited ? "unlimited" : (effectiveIsFullyCovered && !joinMembership) ? "credit" : "stripe",
      price: displayPrice,
    });
    trackMetaEvent("AddToCart", { ...metaEventParams(event as any), num_items: bookingSeats });
    
    
    // Click-time intent only — the paid join (membership_joined_at_checkout)
    // fires on the thank-you page after Stripe confirms payment.
    if (joinMembership) {
      capture("membership_join_selected", {
        event_id: event.id,
        discount_pct: friendsPricing.friendsDiscountPct,
        seats: bookingSeats,
        early_bird_active: friendsPricing.earlyBirdActive,
      });
    }

    onBook({ 
      seats: bookingSeats, 
      // Joining Friends never spends credits — they stay in the account.
      useCredits: joinMembership ? false : effectiveIsFullyCovered,
      event, 
      referralCode: isOwnReferralCode ? undefined : referralCode,
      useUnlimited: shouldUseUnlimited,
      applyCredits: joinMembership ? false : effectiveApplyCredits,
      forFriend: bookingForFriend,
      joinMembership,
      groupId: selectedGroupId,
    });
  }

  const isSoldOut = event.groups?.length
    ? !event.groups.some((group) => group.spots_remaining > 0)
    : (event.booked || 0) >= event.capacity;
  const campaign = findCampaignForEvent(event.id);


  // Calculate URL partner discount amount for display
  let urlPartnerDiscountAmount = 0;
  if (urlPartnerApplies && urlPartnerCodeInfo) {
    if (urlPartnerCodeInfo.discount_type === 'percentage' && urlPartnerCodeInfo.discount_percentage) {
      urlPartnerDiscountAmount = (totalPrice * urlPartnerCodeInfo.discount_percentage) / 100;
    } else if (urlPartnerCodeInfo.discount_type === 'credits') {
      urlPartnerDiscountAmount = urlPartnerCodeInfo.credits_value * CREDIT_VALUE_IN_POUNDS;
    }
  }

  // Calculate display price (what will be charged on card)
  let displayPrice = totalPrice;
  
  // Only show referral discount if valid, not own code, AND not already referred by same person at signup
  // Referral discount is £-denominated → GBP-only events.
  const shouldShowReferralDiscount = eventIsGbp && isValidReferralCode && !isOwnReferralCode && !wasReferredBySameUser;
  
  // Apply URL partner discount first (highest priority alongside campaign)
  if (urlPartnerApplies && urlPartnerCodeInfo) {
    if (urlPartnerCodeInfo.discount_type === 'percentage' && urlPartnerCodeInfo.discount_percentage) {
      displayPrice = displayPrice * (1 - urlPartnerCodeInfo.discount_percentage / 100);
    } else if (urlPartnerCodeInfo.discount_type === 'credits') {
      displayPrice = Math.max(0, displayPrice - urlPartnerCodeInfo.credits_value * CREDIT_VALUE_IN_POUNDS);
    }
  }

  // Apply credits if enabled
  if (effectiveApplyCredits && !effectiveIsFullyCovered) {
    const creditsDiscount = effectiveCreditsToUse * CREDIT_VALUE_IN_POUNDS;
    displayPrice = Math.max(0, displayPrice - creditsDiscount);
    
    // Apply membership discount on remaining (only if no URL partner code)
    if (!urlPartnerApplies) {
      if (memberDiscountApplies && memberDiscountMatch) {
        displayPrice = displayPrice * (1 - memberDiscountMatch.discount.discount_percentage / 100);
      } else if (shouldShowReferralDiscount) {
        displayPrice = Math.max(0, displayPrice - 5);
      }
    }
  } else if (!effectiveApplyCredits) {
    // No credits, apply other discounts (only if no URL partner code)
    if (!urlPartnerApplies) {
      if (memberDiscountApplies && memberDiscountMatch) {
        displayPrice = displayPrice * (1 - memberDiscountMatch.discount.discount_percentage / 100);
      } else if (shouldShowReferralDiscount) {
        displayPrice = Math.max(0, displayPrice - 5);
      }
    }
  }
  
  // ===== Nimi Friends upsell (Phase 3) =====
  // Never stacks with any other discount path. Codes block on mere presence
  // (the raw URL strings), not on whether they apply. Credits do NOT block:
  // selecting the join suppresses credit use instead ("credits stay in your
  // account"). Credit-only events can't take card payment, so stay blocked.
  const otherDiscountInPlay =
    !!urlPartnerApplies ||
    !!urlPartnerCode ||
    !!specialOfferCode ||
    !!campaign ||
    shouldShowReferralDiscount ||
    !eventIsGbp ||
    !stripeAllowed;

  const friendsPricing = useProspectiveMemberPrice({
    event,
    seats,
    blocked: otherDiscountInPlay,
  });
  const [joinMembershipChoice, setJoinMembershipChoice] = useState<boolean | null>(() => {
    // Restore a signed-out visitor's choice after they sign up and return.
    const v = new URLSearchParams(window.location.search).get("joinFriends");
    return v === "1" ? true : v === "0" ? false : null;
  });
  const joinMembership = friendsPricing.show
    ? (joinMembershipChoice ?? friendsPricing.defaultOn)
    : false;

  // Unlimited / tag-restriction flags. Declared before the resume effect below
  // so the resume follows the same blocked-state rule as the Book button.
  const isUnlimited = unlimitedStatus?.isUnlimited;

  // Check if event is within unlimited period (between start and end dates)
  const eventDate = new Date(event.start_time);
  const isEventWithinUnlimitedPeriod = isUnlimited &&
    unlimitedStatus?.startsAt &&
    unlimitedStatus?.expiresAt
      ? eventDate >= unlimitedStatus.startsAt && eventDate <= unlimitedStatus.expiresAt
      : false;

  // Only show unlimited banner if unlimited is allowed for this event and NOT booking for friend
  const showUnlimitedBanner = isUnlimited && isEventWithinUnlimitedPeriod && unlimitedAllowed && !bookingForFriend;

  // Check if this is an unlimited-only event but user is not unlimited
  const isUnlimitedOnlyEvent = unlimitedAllowed && !stripeAllowed && !creditsAllowed;
  const cannotBookUnlimitedOnly = isUnlimitedOnlyEvent && !isUnlimited;

  // Check if user is blocked due to required tags
  const hasRequiredTags = event.required_tags && event.required_tags.length > 0;
  const cannotBookDueToTags = hasRequiredTags && isAuthenticated && !userHasRequiredTag;

  // Blocked / banner-only states keep the old inline pane — there is nothing to
  // configure, so a sheet would add a tap for no reason.
  const blockedState = showUnlimitedBanner || cannotBookUnlimitedOnly || cannotBookDueToTags;

  // After sign-up the visitor lands back here with resumeBooking=1: continue
  // to checkout with the choices they already made instead of asking again.
  // Wait until membership + Friends state are loaded for the new session so
  // the price and checkout path are correct. A blocked state (required tags,
  // unlimited-only, already-unlimited) never resumes — same rule as the Book
  // button. While tags are loading, userHasRequiredTag is false, so the page
  // counts as blocked and the resume waits until eligibility is known.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current || !isAuthenticated) return;
    if (blockedState) return;
    const qs = new URLSearchParams(window.location.search);
    if (qs.get("resumeBooking") !== "1") return;
    if (!membershipFetched || !friendsPricing.isReady) return;
    resumedRef.current = true;
    qs.delete("resumeBooking");
    qs.delete("seats");
    qs.delete("joinFriends");
    qs.delete("group");
    const rest = qs.toString();
    window.history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : ""));
    handleUserBooking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, blockedState, membershipFetched, friendsPricing.isReady]);

  const handleGuestBooking = useCallback(() => {
    trackClick("book_event_guest", { eventId: event.id, eventTitle: event.title });
    trackMetaEvent("AddToCart", { ...metaEventParams(event as any), num_items: seats });
    const params = new URLSearchParams();
    if (referralCode) params.set('referralCode', referralCode);
    if (urlPartnerCode) params.set('partnerCode', urlPartnerCode);
    if (isSecretSalon && hasInvite) params.set('invited', 'true');
    if (friendsPricing.show) params.set('joinFriends', joinMembership ? '1' : '0');
    if (selectedGroupId) params.set('group', selectedGroupId);
    // Resume straight into checkout after sign-up, with the same seats.
    params.set('resumeBooking', '1');
    params.set('seats', String(seats));
    let redirectTo = `/events/${event.id}?${params.toString()}`;
    const signUpPath = `/auth?action=sign-up&referralCode=${referralCode}&redirectTo=${encodeURIComponent(redirectTo)}`;
    navigate(signUpPath);
  }, [event, seats, navigate, referralCode, isSecretSalon, hasInvite, trackClick, urlPartnerCode, friendsPricing.show, joinMembership, selectedGroupId]);


  useEffect(() => {
    if (!friendsPricing.show) return;
    capture("membership_toggle_viewed", {
      event_id: event.id,
      default_state: friendsPricing.defaultOn,
      saving: friendsPricing.saving,
      early_bird_active: friendsPricing.earlyBirdActive,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friendsPricing.show, event.id]);

  // What declining actually costs today — same waterfall as the Book button
  // (credits included), so the toggle and the button never disagree.
  const declineUsesCredits = effectiveApplyCredits && effectiveCreditsToUse > 0;
  const declinePayToday = effectiveApplyCredits && effectiveIsFullyCovered ? 0 : Math.round(displayPrice * 100) / 100;

  if (friendsPricing.show && joinMembership) {
    displayPrice = friendsPricing.joinTotal;
  }


  if (ticketInfo && !bookingForFriend) {

    return (
      <BookedEventActions
        eventId={event.id}
        eventTitle={event.title}
        ticketId={ticketInfo.id}
        onBookMore={() => setBookingForFriend(true)}
        bringFriendEligible={bringFriendEligibility?.eligible ?? false}
      />
    );
  }

  // Show waitlist actions if user has requested
  if (isSecretSalon && waitlistInfo && !hasInvite) {
    return (
      <>
        <WaitlistEventActions eventId={event.id} />
        <WaitlistRequestDialog
          open={showWaitlistDialog}
          onOpenChange={setShowWaitlistDialog}
          eventId={event.id}
          eventTitle={event.title}
          eventDate={new Date(event.start_time)}
          venue={event.venue}
        />
      </>
    );
  }

  // Event has started: no booking, just a notice
  if (new Date(event.start_time).getTime() <= Date.now()) {
    return (
      <div className="fixed bottom-20 left-0 right-0 bg-card border-t border-border px-4 py-3 z-30">
        <p className="max-w-md mx-auto text-center text-sm text-muted-foreground">
          This event is in progress — bookings are closed.
        </p>
      </div>
    );
  }

  // Show campaign waitlist message if event is sold out and has an active campaign
  if (isSoldOut && campaign) {
    return <WaitlistMessage campaign={campaign} />;
  }

  // If Secret Salon without invite, show request button
  if (isSecretSalon && !hasInvite) {
    return (
      <>
        <div className="fixed bottom-20 left-0 right-0 bg-card border-t border-border p-4">
          <div className="max-w-md mx-auto space-y-3">
            <p className="text-sm text-foreground/80 text-center">
              Tickets are {formatPrice(price, event.currency)} and will be allocated in waves to our waitlist.
            </p>
            <Button
              onClick={handleRequestWaitlist}
              disabled={requestWaitlistMutation.isPending}
              className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
              size="lg"
            >
              {requestWaitlistMutation.isPending ? "Submitting..." : "Request an Invite"}
            </Button>
          </div>
        </div>
        <WaitlistRequestDialog
          open={showWaitlistDialog}
          onOpenChange={setShowWaitlistDialog}
          eventId={event.id}
          eventTitle={event.title}
          eventDate={new Date(event.start_time)}
          venue={event.venue}
        />
      </>
    );
  }

  // Determine if credit section should be shown
  const shouldShowCreditSection = isAuthenticated && 
    userMembership && 
    creditsBalance > 0 &&
    creditsCost > 0 && 
    !isCheckingReferralCode && 
    !showUnlimitedBanner && 
    creditsAllowed && 
    stripeAllowed; // Only show toggle if both are available

  const renderBookingBody = () => (
    <>
      {!!event.groups?.length && (
        <DepartureSchedule
          event={event}
          groups={event.groups}
          selectedGroupId={selectedGroupId ?? earliestOpenGroup(event.groups)?.group_id ?? null}
          onSelect={onSelectedGroupChange}
          compact
        />
      )}
      {bringFriendEligibility?.eligible && !bookingForFriend && (
        <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/30 rounded-lg px-3 py-2.5 mb-3 flex items-start gap-2.5">
          <Gift className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-foreground">
                Bring a friend for free
              </span>
              <span className="rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap">
                Included
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Book your ticket to unlock up to 3 free friend invites — whichever option you choose below.
            </p>
          </div>
        </div>
      )}

      {bookingForFriend && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-foreground">Booking additional tickets</span>
            <Button variant="ghost" size="sm" onClick={() => setBookingForFriend(false)} className="text-xs text-muted-foreground h-auto p-1">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {isSecretSalon && hasInvite && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-3 mb-4 text-center">
          <p className="text-sm font-semibold text-success">
            If you can read this page, you've been invited to book!
          </p>
        </div>
      )}

      {/* URL Partner Code discount banner */}
      {urlPartnerApplies && urlPartnerCodeInfo && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mb-3">
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">
              {urlPartnerCodeInfo.checkout_preview_text ||
                `${urlPartnerCodeInfo.partner_name} offer: ${
                  urlPartnerCodeInfo.discount_type === 'percentage'
                    ? `${urlPartnerCodeInfo.discount_percentage}% off`
                    : `£${urlPartnerDiscountAmount.toFixed(2)} off`
                }`
              }
            </span>
          </div>
          {urlPartnerDiscountAmount > 0 && (
            <p className="text-xs text-muted-foreground mt-1 ml-6">
              You save £{urlPartnerDiscountAmount.toFixed(2)} on this booking
            </p>
          )}
        </div>
      )}

      {isUnlimited && isEventWithinUnlimitedPeriod && !unlimitedAllowed && (
        <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/30 rounded-lg p-3 mb-3">
          <div className="flex items-center gap-2">
            <Crown className="h-4 w-4 text-amber-500" />
            <span className="text-xs text-muted-foreground">
              Unlimited booking is not available for this event. Please use another payment method.
            </span>
          </div>
        </div>
      )}

      {isUnlimited && !isEventWithinUnlimitedPeriod && unlimitedAllowed && (
        <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/30 rounded-lg p-3 mb-3">
          <div className="flex items-center gap-2">
            <Crown className="h-4 w-4 text-amber-500" />
            <span className="text-xs text-muted-foreground">
              This event is after your unlimited period ends. Use credits or pay to book.
            </span>
          </div>
        </div>
      )}

      {eventIsGbp ? (
        <ReferralInfo
          referralCode={referralCode}
          isOwnReferralCode={isReady && isOwnReferralCode}
          isValidReferralCode={isValidReferralCode}
          isAuthenticated={isAuthenticated}
        />
      ) : (
        <p className="mb-3 text-xs text-muted-foreground text-center">
          Credits and referral discounts apply to UK events only.
        </p>
      )}

      {friendsPricing.show && (
        <FriendsUpsellToggle
          pricing={friendsPricing}
          seats={seats}
          currency={event.currency}
          joinMembership={joinMembership}
          hasCredits={effectiveApplyCredits && effectiveCreditsToUse > 0}
          declineTotal={declinePayToday}
          declineCreditsUsed={declineUsesCredits ? effectiveCreditsToUse : 0}
          onChange={(next) => {
            setJoinMembershipChoice(next);
            capture("membership_toggle_changed", {
              event_id: event.id,
              new_state: next,
              early_bird_active: friendsPricing.earlyBirdActive,
            });
          }}
        />
      )}

      {shouldShowCreditSection && !joinMembership && (
        <EventCreditInfo
          creditsBalance={creditsBalance}
          totalCreditCost={effectiveCreditCost}
          originalCreditCost={totalCreditCost !== effectiveCreditCost ? totalCreditCost : undefined}
          eventPrice={unitPrice}
          effectivePrice={effectivePrice}
          seats={seats}
          applyCredits={applyCredits}
          onToggleCredits={setApplyCredits}
          discountLabel={memberDiscountApplies && !urlPartnerApplies ? memberDiscountMatch?.label : undefined}
        />
      )}

      {/* Message for credit-only events */}
      {isCreditOnlyEvent && isAuthenticated && userMembership && creditsCost > 0 && (
        <div className="bg-muted/50 border border-border rounded-lg p-3 mb-3">
          <p className="text-sm text-muted-foreground">
            This event will be booked using your credits ({totalCreditCost} credits required).
          </p>
        </div>
      )}

      <BookingControls
        seats={seats}
        onSeatsChange={setSeats}
        remainingSeats={remainingSeats}
        onBook={isAuthenticated ? handleUserBooking : handleGuestBooking}
        isProcessing={isProcessing || isLoadingAuthInfo}
        isFullyCoveredByCredits={joinMembership ? false : effectiveIsFullyCovered}
        creditsToUse={joinMembership ? 0 : effectiveCreditsToUse}
        displayPrice={displayPrice}
        bookLabel={joinMembership ? "Book & Subscribe" : "Book"}
        disabled={isCreditOnlyEvent && creditsBalance < totalCreditCost}
        isSoldOut={isSoldOut}
        currency={event.currency}
        onNotifyMe={() => {
          if (!isAuthenticated) {
            const redirectTo = `/events/${event.id}`;
            navigate(`/auth?action=sign-up&redirectTo=${encodeURIComponent(redirectTo)}`);
            return;
          }
          capture("waitlist_joined", { event_id: event.id });
          registerInterest.mutate(undefined, {
            onSuccess: () => {
              toast({ title: "You'll be notified when spots become available" });
            },
            onError: () => {
              toast({ title: "Something went wrong", variant: "destructive" });
            },
          });
        }}
        hasInterest={hasInterest}
        isRegisteringInterest={registerInterest.isPending}
        isLoadingInterest={isLoadingInterest}
        onCancelInterest={() => {
          cancelInterest.mutate(undefined, {
            onSuccess: () => {
              toast({ title: "Interest cancelled" });
            },
            onError: () => {
              toast({ title: "Something went wrong", variant: "destructive" });
            },
          });
        }}
        isCancellingInterest={cancelInterest.isPending}
      />
    </>
  );

  const barFullyCovered = !joinMembership && effectiveApplyCredits && effectiveIsFullyCovered;

  if (!blockedState) {
    return (
      <>
        <div className="fixed bottom-20 left-0 right-0 bg-card border-t border-border px-4 py-2.5 z-30">
          <div className="max-w-md mx-auto flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-base font-semibold text-foreground leading-tight">
                {barFullyCovered ? "Free with credits" : formatPrice(displayPrice, event.currency)}
              </div>
              {bringFriendEligibility?.eligible && !bookingForFriend ? (
                <span className="inline-flex items-center gap-1 mt-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                  <Gift className="h-3 w-3" />
                  1+1 Free
                </span>
              ) : (
                <div className="text-xs text-muted-foreground leading-tight">
                  {selectedGroup ? `${formatGroupTime(selectedGroup.start_time, event.timezone)} · ` : ""}{seats} {seats === 1 ? "ticket" : "tickets"}
                </div>
              )}
            </div>
            <Button
              size="lg"
              className="shrink-0"
              onClick={() => setShowBookingSheet(true)}
              disabled={isProcessing || isLoadingAuthInfo}
            >
              {isSoldOut ? "See options" : "Choose tickets"}
            </Button>
          </div>
        </div>

        <Sheet open={showBookingSheet} onOpenChange={setShowBookingSheet}>
          <SheetContent
            side="bottom"
            className="p-0 rounded-t-2xl max-h-[85vh] overflow-y-auto"
          >
            <div className="p-4 pb-6">
              <SheetHeader className="mb-3 text-left">
                <SheetTitle className="text-base leading-snug pr-8">{event.title}</SheetTitle>
              </SheetHeader>
              <div className="max-w-md mx-auto">
                {renderBookingBody()}
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <div className="fixed bottom-20 left-0 right-0 bg-card border-t border-border p-4">
      <div className="max-w-md mx-auto">
        {showUnlimitedBanner && (
          <UnlimitedBookingBanner
            onBook={handleUserBooking}
            isProcessing={isProcessing}
            isSoldOut={isSoldOut}
            daysRemaining={unlimitedStatus.daysRemaining}
            eventStartTime={event.start_time}
            unlimitedExpiresAt={unlimitedStatus.expiresAt}
          />
        )}

        {/* Show message when only unlimited is allowed but user is not unlimited */}
        {cannotBookUnlimitedOnly && (
          <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="h-5 w-5 text-amber-500" />
              <span className="font-semibold text-foreground">Unlimited Members Only</span>
            </div>
            <p className="text-sm text-muted-foreground">
              This event is exclusively available to Unlimited members. 
              Upgrade to Unlimited to access this and all other events for free.
            </p>
            <Button
              className="mt-3 w-full"
              variant="outline"
              onClick={() => navigate('/offers')}
            >
              View Unlimited Plans
            </Button>
          </div>
        )}

        {/* Show message when user doesn't have required tag */}
        {cannotBookDueToTags && !cannotBookUnlimitedOnly && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              <span className="font-semibold text-foreground">Restricted Event</span>
            </div>
            <p className="text-sm text-muted-foreground">
              This event is only available to users with the {event.required_tags?.map(t => `"${t}"`).join(" or ")} status.
            </p>
          </div>
        )}
      </div>
    </div>
  );

};

export default EventBooking;
